/**
 * FASE 20.2 — Visits Query Adapter
 *
 * INVARIANT: never select("*"), never raw SQL, always limit(), always order().
 */

import { supabase }              from "../../supabase";
import { applyPermissionScope }  from "../permissionFilters";

const VISIT_FIELDS = "id, date, status, visit_type, contact_name, target_family_id, target_person_id, next_visit_date, completed_at";
const MAX_ROWS     = 25;

/**
 * Visits scheduled for today.
 * @param {import('../permissionFilters').PermissionScope} scope
 * @returns {Promise<Object[]>}
 */
export async function getTodayVisits(scope) {
  const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  let query = supabase
    .from("visits")
    .select(VISIT_FIELDS)
    .eq("date", today)
    .order("date", { ascending: true })
    .limit(MAX_ROWS);

  query = applyPermissionScope(query, scope);
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

/**
 * Recent visit history for a specific family or person.
 * @param {import('../permissionFilters').PermissionScope} scope
 * @param {{ entity_id: string }} filters
 * @returns {Promise<Object[]>}
 */
export async function getVisitTimeline(scope, filters) {
  const { entity_id } = filters ?? {};
  if (!entity_id) return [];

  const threeMonthsAgo = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

  // Try family match first, then person match — entity could be either type
  const { data: familyData, error: familyErr } = await supabase
    .from("visits")
    .select(VISIT_FIELDS)
    .eq("target_family_id", entity_id)
    .gte("date", threeMonthsAgo)
    .order("date", { ascending: false })
    .limit(10);

  if (!familyErr && familyData?.length > 0) return familyData;

  const { data: personData, error: personErr } = await supabase
    .from("visits")
    .select(VISIT_FIELDS)
    .eq("target_person_id", entity_id)
    .gte("date", threeMonthsAgo)
    .order("date", { ascending: false })
    .limit(10);

  if (personErr) throw personErr;
  return personData ?? [];
}

/**
 * Upcoming pending visits — today onwards, next N days.
 * Use when the user asks about "próximas visitas" or "visitas pendientes".
 * @param {import('../permissionFilters').PermissionScope} scope
 * @param {number} [daysAhead=7]
 * @returns {Promise<Object[]>}
 */
export async function getUpcomingVisits(scope, daysAhead = 7) {
  const today  = new Date().toISOString().slice(0, 10);
  const future = new Date(Date.now() + daysAhead * 86_400_000).toISOString().slice(0, 10);

  let query = supabase
    .from("visits")
    .select(VISIT_FIELDS)
    .gte("date", today)
    .lte("date", future)
    .eq("status", "pendiente")
    .order("date", { ascending: true })
    .limit(15);

  query = applyPermissionScope(query, scope);
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

/**
 * The very next scheduled (pending) visit.
 * Use when the user asks "¿cuál es mi próxima visita?".
 * Returns up to 3 rows so the LLM can mention the next few.
 * @param {import('../permissionFilters').PermissionScope} scope
 * @returns {Promise<Object[]>}
 */
export async function getNextVisit(scope) {
  const today = new Date().toISOString().slice(0, 10);

  let query = supabase
    .from("visits")
    .select(VISIT_FIELDS)
    .gte("date", today)
    .eq("status", "pendiente")
    .order("date", { ascending: true })
    .limit(3);

  query = applyPermissionScope(query, scope);
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

/**
 * Church activity summary: recent + upcoming visits count.
 * @param {import('../permissionFilters').PermissionScope} scope
 * @returns {Promise<Object[]>}
 */
export async function getChurchVisitActivity(scope) {
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);

  let query = supabase
    .from("visits")
    .select("id, date, status, visit_type, contact_name")
    .gte("date", sevenDaysAgo)
    .order("date", { ascending: false })
    .limit(10);

  query = applyPermissionScope(query, scope);
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}
